export type QuestionFlow = "business" | "personal";
export type FlowFilter = QuestionFlow | "all";
export type BankView = "custom" | "default";

export type BankOption = {
  text: string;
  tags?: string[];
  bumps?: string[];
  follow_up?: string;
};

export type AnswerType =
  | "yes_no"
  | "multi_select"
  | "dropdown"
  | "number"
  | "currency"
  | "text"
  | "yes_no_list";

export type BankQuestion = {
  key: string;
  screen: string;
  flow: QuestionFlow;
  text: string;
  answer_type: AnswerType;
  help_text: string | null;
  options: BankOption[];
  rules: string[];
  position: number;
  active: boolean;
  source: "default" | "custom";
  editable: boolean;
  node: string | null;
};

export type BankScreen = {
  key: string;
  title: string;
};

export type BankPayload = {
  screens: BankScreen[];
  questions: BankQuestion[];
};

export type BankColumn = {
  key: string;
  title: string;
  cards: BankQuestion[];
};

export type CardMove = {
  key: string;
  fromColumn: string;
  fromIndex: number;
  toColumn: string;
  toIndex: number;
};

export type MovePlan =
  | { type: "none" }
  | { type: "reorder"; keys: string[] }
  | { type: "place"; key: string; screen: string; keys: string[] }
  | { type: "hide"; key: string };

export const FLOW_LABELS: Record<QuestionFlow, string> = {
  business: "Business",
  personal: "Personal",
};

export const QUESTION_TEXT_LIMIT = 500;
export const MIN_ANSWER_OPTIONS = 2;

export const ANSWER_TYPE_LABELS: Record<AnswerType, string> = {
  yes_no: "Yes / No",
  multi_select: "Select all that apply",
  dropdown: "Dropdown",
  number: "Number",
  currency: "Amount",
  text: "Free text",
  yes_no_list: "Yes / No per item",
};

// A firm picks between these two; the others only describe what the default questions already ask.
export const CUSTOM_ANSWER_TYPES: AnswerType[] = ["yes_no", "multi_select"];

export const parseOptionLines = (value: string): string[] =>
  value
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line !== "");

export const isNewQuestionValid = (
  text: string,
  answerType: AnswerType,
  optionLines: string
): boolean => {
  if (text.trim() === "") {
    return false;
  }
  return (
    answerType !== "multi_select" ||
    parseOptionLines(optionLines).length >= MIN_ANSWER_OPTIONS
  );
};

// The server's screen keys come from config/question_bank.yml, which has no screen with this key.
export const BANK_COLUMN_KEY = "question_bank";
export const BANK_COLUMN_TITLE = "Question Bank";

const byPosition = (left: BankQuestion, right: BankQuestion): number =>
  left.position - right.position || left.key.localeCompare(right.key);

const inScreen = (questions: BankQuestion[], screen: string): BankQuestion[] =>
  questions.filter((question) => question.screen === screen);

// Defaults and firm questions have their own position sequences, so they are never sorted together:
// the defaults keep the order of the board and the firm's questions come after them.
export const defaultsInScreen = (
  questions: BankQuestion[],
  screen: string
): BankQuestion[] =>
  inScreen(questions, screen)
    .filter((question) => question.source === "default")
    .sort(byPosition);

export const customInScreen = (
  questions: BankQuestion[],
  screen: string
): BankQuestion[] =>
  inScreen(questions, screen)
    .filter((question) => question.source === "custom" && question.active)
    .sort(byPosition);

export const matchesFlow = (question: BankQuestion, flow: FlowFilter): boolean =>
  flow === "all" || question.flow === flow;

export const buildBoard = (
  payload: BankPayload | null,
  flow: FlowFilter,
  withBank: boolean
): BankColumn[] => {
  if (!payload) {
    return [];
  }

  const visible = (cards: BankQuestion[]) =>
    cards.filter((question) => matchesFlow(question, flow));

  const categories = payload.screens.map((screen) => ({
    key: screen.key,
    title: screen.title,
    cards: [
      ...visible(defaultsInScreen(payload.questions, screen.key)),
      ...visible(customInScreen(payload.questions, screen.key)),
    ],
  }));

  if (!withBank) {
    return categories;
  }

  const screenOrder = payload.screens.map((screen) => screen.key);
  const bankCards = payload.questions
    .filter(
      (question) =>
        question.source === "custom" && !question.active && matchesFlow(question, flow)
    )
    .sort(
      (left, right) =>
        screenOrder.indexOf(left.screen) - screenOrder.indexOf(right.screen) ||
        byPosition(left, right)
    );

  return [{ key: BANK_COLUMN_KEY, title: BANK_COLUMN_TITLE, cards: bankCards }, ...categories];
};

// The board shows only the flows the filter lets through, but the server stores the order of the
// whole column. The helpers below move cards among the visible ones and leave the hidden ones where
// they are.
export const reorderVisible = (
  allKeys: string[],
  visibleKeys: string[],
  fromIndex: number,
  toIndex: number
): string[] => {
  const visibleOrder = visibleKeys.slice();
  const [moved] = visibleOrder.splice(fromIndex, 1);
  visibleOrder.splice(toIndex, 0, moved);

  const reordered = allKeys.slice();
  allKeys
    .map((key, index) => (visibleKeys.includes(key) ? index : -1))
    .filter((index) => index !== -1)
    .forEach((slot, visibleIndex) => {
      reordered[slot] = visibleOrder[visibleIndex];
    });
  return reordered;
};

export const insertAmongVisible = (
  allKeys: string[],
  visibleKeys: string[],
  key: string,
  toIndex: number
): string[] => {
  const keys = allKeys.filter((existing) => existing !== key);
  const nextVisible = visibleKeys[toIndex];

  if (nextVisible !== undefined) {
    keys.splice(keys.indexOf(nextVisible), 0, key);
    return keys;
  }

  const lastVisible = visibleKeys[visibleKeys.length - 1];
  const insertAt = lastVisible === undefined ? keys.length : keys.indexOf(lastVisible) + 1;
  keys.splice(insertAt, 0, key);
  return keys;
};

export const moveAmongVisible = (
  allKeys: string[],
  visibleKeys: string[],
  key: string,
  direction: -1 | 1
): string[] | null => {
  const fromIndex = visibleKeys.indexOf(key);
  const toIndex = fromIndex + direction;
  if (fromIndex === -1 || toIndex < 0 || toIndex >= visibleKeys.length) {
    return null;
  }
  return reorderVisible(allKeys, visibleKeys, fromIndex, toIndex);
};

// Default questions feed the pricing logic, so they never move. Only the questions a firm added can
// be dropped somewhere else, and a category column always lists its defaults before them.
export const planCardMove = (
  questions: BankQuestion[],
  flow: FlowFilter,
  move: CardMove
): MovePlan => {
  const { key, fromColumn, fromIndex, toColumn, toIndex } = move;
  const question = questions.find((candidate) => candidate.key === key);

  if (!question || !question.editable) {
    return { type: "none" };
  }

  if (toColumn === BANK_COLUMN_KEY) {
    return fromColumn === BANK_COLUMN_KEY ? { type: "none" } : { type: "hide", key };
  }

  const defaultsShown = defaultsInScreen(questions, toColumn).filter((candidate) =>
    matchesFlow(candidate, flow)
  ).length;
  const custom = customInScreen(questions, toColumn);
  const allKeys = custom.map((candidate) => candidate.key);
  const visibleKeys = custom
    .filter((candidate) => matchesFlow(candidate, flow))
    .map((candidate) => candidate.key);
  const target = Math.max(0, toIndex - defaultsShown);

  if (fromColumn === toColumn) {
    const from = Math.max(0, fromIndex - defaultsShown);
    const to = Math.min(target, visibleKeys.length - 1);
    return from === to
      ? { type: "none" }
      : { type: "reorder", keys: reorderVisible(allKeys, visibleKeys, from, to) };
  }

  return {
    type: "place",
    key,
    screen: toColumn,
    keys: insertAmongVisible(allKeys, visibleKeys, key, target),
  };
};
